import React, { useState, lazy, Suspense, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AppSection } from './types';
import { useDarkMode } from './hooks/useLocalStorage';

const CourtDiagram = lazy(() => import('./components/CourtDiagram'));
const ScoringSimulator = lazy(() => import('./components/ScoringSimulator'));
const MiniGames = lazy(() => import('./components/MiniGames'));
const GearGuide = lazy(() => import('./components/GearGuide'));

const NAVIGATION_ITEMS = [
  { section: AppSection.OVERVIEW, label: 'Home', icon: '\u{1F3E0}', gradient: 'from-blue-500 to-cyan-500' },
  { section: AppSection.COURT, label: 'The Court', icon: '\u{1F3DF}', gradient: 'from-green-500 to-emerald-500' },
  { section: AppSection.RULES, label: 'Rules', icon: '\u{1F4D6}', gradient: 'from-purple-500 to-pink-500' },
  { section: AppSection.GEAR, label: 'Gear', icon: '\u{1F3BE}', gradient: 'from-teal-500 to-green-500' },
  { section: AppSection.SCORING, label: 'Scoring', icon: '\u{1F4CA}', gradient: 'from-orange-500 to-red-500' },
  { section: AppSection.GAMES, label: 'Games', icon: '\u{1F3AE}', gradient: 'from-yellow-500 to-orange-500' },
];

const HOME_TILES = [
  { icon: '\u{1F3DF}', title: 'The Court', desc: 'Explore every zone of the court with detailed rule explanations', color: 'from-green-400 to-emerald-500', target: AppSection.COURT },
  { icon: '\u{1F4D6}', title: 'Rules', desc: 'A clear, fast guide to how Padel is played', color: 'from-purple-400 to-pink-500', target: AppSection.RULES },
  { icon: '\u{1F3BE}', title: 'Gear', desc: 'Everything you need to know about padel equipment, from rackets to court shoes', color: 'from-teal-400 to-green-500', target: AppSection.GEAR },
  { icon: '\u{1F4CA}', title: 'Scoring', desc: 'Master the scoring system with interactive simulations', color: 'from-blue-400 to-cyan-500', target: AppSection.SCORING },
  { icon: '\u{1F3AE}', title: 'Games', desc: 'Test your knowledge with quizzes and mini challenges', color: 'from-yellow-400 to-orange-500', target: AppSection.GAMES },
];

interface TileCardProps {
  item: typeof HOME_TILES[0];
  index: number;
  onSelect: (target: AppSection) => void;
}

const TileCard: React.FC<TileCardProps> = ({ item, index, onSelect }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.1 + index * 0.08 }}
    whileHover={{ y: -4, transition: { duration: 0.2 } }}
    onClick={() => onSelect(item.target)}
    className="bg-white dark:bg-slate-800 rounded-3xl p-4 sm:p-8 shadow-lg hover:shadow-2xl transition-all group cursor-pointer border border-slate-100 dark:border-slate-700"
  >
    <div className={`inline-flex items-center justify-center w-10 h-10 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br ${item.color} mb-3 sm:mb-5 text-lg sm:text-2xl shadow-lg group-hover:scale-110 transition-transform`}>
      {item.icon}
    </div>
    <h3 className="text-lg sm:text-2xl font-display font-bold text-slate-900 dark:text-white mb-2">{item.title}</h3>
    <p className="text-sm sm:text-base text-slate-500 dark:text-slate-400 leading-relaxed">{item.desc}</p>
  </motion.div>
);

interface AppHeaderProps {
  section: AppSection;
  onNavigate: (s: AppSection) => void;
  isDark: boolean;
  onToggleDark: () => void;
}

const AppHeader: React.FC<AppHeaderProps> = ({ section, onNavigate, isDark, onToggleDark }) => {
  const [scrolled, setScrolled] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [canHover, setCanHover] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setCanHover(window.matchMedia('(hover: hover)').matches);
  }, []);

  return (
    <motion.header
      className="fixed top-9 left-0 right-0 z-40 px-4 pt-4"
      animate={{ opacity: scrolled && !hovered && canHover ? 0.3 : 1 }}
      transition={{ duration: 0.25, ease: 'easeInOut' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="max-w-7xl mx-auto">
        <div className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-xl rounded-3xl shadow-lg shadow-slate-900/5 border border-slate-200/50 dark:border-slate-700/50">
          <div className="px-4 py-2 lg:px-6 lg:py-4 flex items-center justify-between">
            <motion.div
              whileHover={{ scale: 1.05 }}
              onClick={() => onNavigate(AppSection.OVERVIEW)}
              className="flex items-center gap-3 cursor-pointer group"
            >
              <div className="w-9 h-9 lg:w-12 lg:h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center text-2xl shadow-lg shadow-blue-500/20 group-hover:shadow-blue-500/40 transition-all group-hover:rotate-6">
                {'\u{1F3BE}'}
              </div>
              <div>
                <h1 className="text-xl lg:text-2xl font-display font-black text-slate-900 dark:text-white leading-none">PadelPro</h1>
                <p className="hidden lg:block text-xs text-slate-500 dark:text-slate-400 font-medium">Learn. Practice. Master.</p>
              </div>
            </motion.div>

            <div className="flex items-center gap-3 lg:gap-4">
              <nav className="hidden lg:flex items-center gap-2">
                {NAVIGATION_ITEMS.map((item) => (
                  <motion.button
                    key={item.section}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => onNavigate(item.section)}
                    className={`relative px-5 py-2.5 rounded-xl font-bold text-sm transition-all ${
                      section === item.section
                        ? 'text-white'
                        : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700'
                    }`}
                  >
                    {section === item.section && (
                      <motion.div
                        layoutId="activeSection"
                        className={`absolute inset-0 bg-gradient-to-r ${item.gradient} rounded-xl shadow-lg`}
                        transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                      />
                    )}
                    <span className="relative flex items-center gap-2">
                      <span>{item.icon}</span>
                      <span>{item.label}</span>
                    </span>
                  </motion.button>
                ))}
              </nav>
              <DarkModeToggle isDark={isDark} onToggle={onToggleDark} />
            </div>
          </div>
        </div>
      </div>
    </motion.header>
  );
};

type RulesTab = 'core' | 'serving' | 'wall';

interface RulesPageProps {
  coreRules: { id: string; title: string; content: string }[];
  serveChecklist: string[];
  wallQuizItems: { id: string; statement: string; answer: 'in' | 'out'; detail: string }[];
  wallQuizAnswers: Record<string, 'in' | 'out' | null>;
  setWallQuizAnswers: React.Dispatch<React.SetStateAction<Record<string, 'in' | 'out' | null>>>;
}

const RULES_TABS: { id: RulesTab; label: string }[] = [
  { id: 'core', label: 'Core Rules' },
  { id: 'serving', label: 'Serving' },
  { id: 'wall', label: 'Wall Play' },
];

const RulesPage: React.FC<RulesPageProps> = ({
  coreRules,
  serveChecklist,
  wallQuizItems,
  wallQuizAnswers,
  setWallQuizAnswers,
}) => {
  const [activeTab, setActiveTab] = useState<RulesTab>('core');

  const CoreRulesSection = (
    <section className="bg-white dark:bg-slate-800 rounded-3xl shadow-xl p-6 sm:p-10">
      <h3 className="text-xl sm:text-3xl font-display font-bold text-slate-900 dark:text-white mb-6">Core Rules</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {coreRules.map((rule, idx) => (
          <div
            key={rule.id}
            className="relative overflow-hidden rounded-3xl border border-slate-100 dark:border-slate-700 bg-gradient-to-br from-slate-50 to-white dark:from-slate-700 dark:to-slate-800 p-6 shadow-lg"
          >
            <div className="absolute -right-6 -top-6 w-20 h-20 rounded-full bg-blue-500/10" />
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 flex-shrink-0 rounded-2xl bg-blue-500 text-white text-xl font-black flex items-center justify-center">
                {idx + 1}
              </div>
              <h4 className="text-lg sm:text-2xl font-display font-bold text-slate-900 dark:text-white">{rule.title}</h4>
            </div>
            <p className="text-base sm:text-lg text-slate-700 dark:text-slate-300 leading-relaxed">{rule.content}</p>
          </div>
        ))}
      </div>
    </section>
  );

  const ServingSection = (
    <section className="bg-white dark:bg-slate-800 rounded-3xl shadow-xl p-6 sm:p-10">
      <h3 className="text-xl sm:text-3xl font-display font-bold text-slate-900 dark:text-white mb-6">Serving</h3>
      <div className="space-y-3">
        {serveChecklist.map((item, idx) => (
          <div key={idx} className="flex items-center gap-4 bg-slate-50 dark:bg-slate-700 rounded-2xl p-4 border border-slate-100 dark:border-slate-600">
            <div className="w-9 h-9 flex-shrink-0 rounded-2xl bg-blue-500 text-white font-black flex items-center justify-center">
              {idx + 1}
            </div>
            <p className="text-slate-700 dark:text-slate-300 leading-relaxed">{item}</p>
          </div>
        ))}
      </div>
    </section>
  );

  const WallPlaySection = (
    <section className="bg-white dark:bg-slate-800 rounded-3xl shadow-xl p-6 sm:p-10">
      <h3 className="text-xl sm:text-3xl font-display font-bold text-slate-900 dark:text-white mb-2">Wall Play Guide</h3>
      <p className="text-slate-700 dark:text-slate-300 text-base sm:text-lg leading-relaxed mb-4">
        You can use the walls to your advantage in two ways. You can either let the ball bounce and hit the wall before hitting it back, or you can hit the ball against the wall to try and land it on your opponents' side of the court. In a rally you can also aim your shots to bounce and hit the cage to create awkward rebounds for your opponents.
      </p>
      <p className="text-slate-600 dark:text-slate-400 mb-6">
        Decide if each scenario is IN or OUT. Tap a choice to reveal the result.
      </p>
      <div className="space-y-4">
        {wallQuizItems.map((item) => {
          const selection = wallQuizAnswers[item.id] ?? null;
          const isCorrect = selection === item.answer;
          return (
            <div key={item.id} className="border border-slate-100 dark:border-slate-700 rounded-2xl p-5">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="text-slate-800 dark:text-slate-200 font-medium">{item.statement}</div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setWallQuizAnswers((prev) => ({ ...prev, [item.id]: 'in' }))}
                    className={`px-4 py-2 rounded-xl font-bold border transition-colors ${
                      selection === 'in'
                        ? 'bg-emerald-500 text-white border-emerald-500'
                        : 'bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-600 hover:border-emerald-400'
                    }`}
                  >
                    IN
                  </button>
                  <button
                    onClick={() => setWallQuizAnswers((prev) => ({ ...prev, [item.id]: 'out' }))}
                    className={`px-4 py-2 rounded-xl font-bold border transition-colors ${
                      selection === 'out'
                        ? 'bg-rose-500 text-white border-rose-500'
                        : 'bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-600 hover:border-rose-400'
                    }`}
                  >
                    OUT
                  </button>
                </div>
              </div>
              {selection && (
                <div className={`mt-3 text-sm font-medium ${isCorrect ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {isCorrect ? 'Correct.' : 'Not quite.'} {item.detail}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );

  const tabContent: Record<RulesTab, React.ReactNode> = {
    core: CoreRulesSection,
    serving: ServingSection,
    wall: WallPlaySection,
  };

  return (
    <div className="max-w-6xl mx-auto space-y-14">
      {/* Mobile: heading + tab strip */}
      <div className="sm:hidden">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-2">Rules of Play</h2>
          <p className="text-base text-slate-600 dark:text-slate-400">A clear, fast guide to how Padel works</p>
        </div>
        <div className="flex gap-2 mb-4">
          {RULES_TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 px-3 py-2 rounded-xl text-sm font-bold transition-colors ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                  : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            {tabContent[activeTab]}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Desktop: full stacked layout */}
      <div className="hidden sm:block space-y-14">
        <div className="text-center mb-12">
          <h2 className="text-2xl sm:text-5xl font-display font-bold text-slate-900 dark:text-white mb-4">Rules of Play</h2>
          <p className="text-base sm:text-xl text-slate-600 dark:text-slate-400">A clear, fast guide to how Padel works</p>
        </div>
        {CoreRulesSection}
        {ServingSection}
        {WallPlaySection}
      </div>
    </div>
  );
};

const App: React.FC = () => {
  const [section, setSection] = useState<AppSection>(AppSection.OVERVIEW);
  const [wallQuizAnswers, setWallQuizAnswers] = useState<Record<string, 'in' | 'out' | null>>({});
  const { isDark, toggle: toggleDark } = useDarkMode();

  const coreRules = [
    {
      id: 'starting-play',
      title: 'Starting Play',
      content: 'Every point starts with an underarm serve. The server must bounce the ball, strike below waist height, and send it diagonally into the opposite service box.'
    },
    {
      id: 'in-vs-out',
      title: 'In vs Out',
      content: 'The ball must bounce in the court before touching any wall or cage. A direct hit on glass or metal without a bounce is out.'
    },
    {
      id: 'bounce-rule',
      title: 'The Bounce Rule',
      content: 'One bounce maximum on your side. If the ball bounces twice before you hit it, the point is lost immediately.'
    },
    {
      id: 'out-of-bounds',
      title: 'Out-of-Bounds Returns',
      content: 'After a bounce, the ball can fly out of the court. Chase it through the side doors and return it before it bounces again.'
    }
  ];

  const serveChecklist = [
    'Setup: stand behind the service line and serve cross-court into the opposite box.',
    'Technique: bounce first, strike underhand below waist height, and keep one foot grounded.',
    'Serve landing: wall after the bounce is live; cage after the bounce is out.',
    'Second chances: net cord in is a let; two missed serves give the point away.',
    'Rotation: serve the entire game, then switch servers.'
  ];

  const wallQuizItems: { id: string; statement: string; answer: 'in' | 'out'; detail: string }[] = [
    {
      id: 'bounce-back-glass',
      statement: 'Bounce then back glass (even on serve).',
      answer: 'in',
      detail: 'If the ball bounces in first, it can hit the back glass and stay live.'
    },
    {
      id: 'bounce-cage-rally',
      statement: 'Bounce then cage during a rally.',
      answer: 'in',
      detail: 'After a bounce in play, a cage touch is still live in rallies.'
    },
    {
      id: 'bounce-over',
      statement: 'Bounce then over the glass or cage.',
      answer: 'in',
      detail: 'If it bounces in and then exits, it is still in play.'
    },
    {
      id: 'direct-wall',
      statement: 'Direct wall or cage hit with no bounce.',
      answer: 'out',
      detail: 'A direct hit on glass or metal without a bounce is out.'
    },
    {
      id: 'double-bounce-wall',
      statement: 'Double bounce, then hits a wall.',
      answer: 'out',
      detail: 'Two bounces ends the point before any wall contact matters.'
    },
    {
      id: 'serve-cage',
      statement: 'Serve hits the cage after the bounce.',
      answer: 'out',
      detail: 'On serve, a bounce followed by cage contact is out.'
    }
  ];

  const renderContent = () => {
    const pageVariants = {
      initial: { opacity: 0, y: 20 },
      animate: { opacity: 1, y: 0 },
      exit: { opacity: 0, y: -20 }
    };

    switch (section) {
      case AppSection.OVERVIEW:
        return (
          <motion.div
            key="overview"
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.3 }}
            className="pt-6 pb-28 px-4 sm:pt-12 sm:px-6 lg:pb-0 bg-slate-50 dark:bg-slate-900 min-h-screen"
          >
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-10">
                <h1 className="text-2xl sm:text-4xl md:text-5xl font-display font-black text-slate-900 dark:text-white mb-3">
                  Master Padel Rules
                </h1>
                <p className="text-base sm:text-lg text-slate-600 dark:text-slate-300 mb-1">
                  Padel is fast, social, and easy to pick up - but the rules catch everyone out at first.
                </p>
                <p className="text-sm sm:text-base text-slate-500 dark:text-slate-400">
                  Pick a section below to get started.
                </p>
              </div>

              <div className="space-y-6">
                {/* Row 1: 3 tiles */}
                <div className="hidden md:grid md:grid-cols-3 gap-6">
                  {HOME_TILES.slice(0, 3).map((item, i) => (
                    <TileCard key={item.title} item={item} index={i} onSelect={setSection} />
                  ))}
                </div>
                {/* Row 2: 2 tiles staggered (offset by half a column) */}
                <div className="hidden md:grid md:grid-cols-12 gap-6">
                  <div className="col-span-4 col-start-3">
                    <TileCard item={HOME_TILES[3]} index={3} onSelect={setSection} />
                  </div>
                  <div className="col-span-4 col-start-7">
                    <TileCard item={HOME_TILES[4]} index={4} onSelect={setSection} />
                  </div>
                </div>
                {/* Mobile: single column */}
                <div className="md:hidden space-y-4">
                  {HOME_TILES.map((item, i) => (
                    <TileCard key={item.title} item={item} index={i} onSelect={setSection} />
                  ))}
                </div>
              </div>

            </div>
          </motion.div>
        );

      case AppSection.COURT:
        return (
          <motion.div
            key="court"
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="pt-6 pb-28 px-4 sm:pt-12 sm:px-6 lg:pb-0 bg-slate-50 dark:bg-slate-900 min-h-screen"
          >
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-2xl sm:text-5xl font-display font-bold text-slate-900 dark:text-white mb-4">Interactive Court</h2>
                <p className="text-base sm:text-xl text-slate-600 dark:text-slate-400">Click on different zones to explore the rules</p>
              </div>
              <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-xl p-6 md:p-10">
                <Suspense fallback={<LoadingSpinner />}>
                  <CourtDiagram />
                </Suspense>
              </div>
            </div>
          </motion.div>
        );

      case AppSection.RULES:
        return (
          <motion.div
            key="rules"
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="pt-6 pb-28 px-4 sm:pt-12 sm:px-6 lg:pb-0 bg-slate-50 dark:bg-slate-900 min-h-screen"
          >
            <RulesPage
              coreRules={coreRules}
              serveChecklist={serveChecklist}
              wallQuizItems={wallQuizItems}
              wallQuizAnswers={wallQuizAnswers}
              setWallQuizAnswers={setWallQuizAnswers}
            />
          </motion.div>
        );

      case AppSection.GEAR:
        return (
          <motion.div
            key="gear"
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.3 }}
            className="pt-6 pb-28 px-4 sm:pt-12 sm:px-6 lg:pb-0 bg-slate-50 dark:bg-slate-900 min-h-screen"
          >
            <Suspense fallback={<LoadingSpinner />}>
              <GearGuide />
            </Suspense>
          </motion.div>
        );

      case AppSection.SCORING:
        return (
          <motion.div
            key="scoring"
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="pt-6 pb-28 px-4 sm:pt-12 sm:px-6 lg:pb-0 bg-slate-50 dark:bg-slate-900 min-h-screen"
          >
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-2xl sm:text-5xl font-display font-bold text-slate-900 dark:text-white mb-4">Scoring Simulator</h2>
                <p className="text-base sm:text-xl text-slate-600 dark:text-slate-400">Practice and master the Padel scoring system</p>
              </div>
              <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-xl p-6 md:p-10">
                <Suspense fallback={<LoadingSpinner />}>
                  <ScoringSimulator />
                </Suspense>
              </div>
            </div>
          </motion.div>
        );

      case AppSection.GAMES:
        return (
          <motion.div
            key="games"
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="pt-6 pb-28 px-4 sm:pt-12 sm:px-6 lg:pb-0 bg-slate-50 dark:bg-slate-900 min-h-screen"
          >
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-2xl sm:text-5xl font-display font-bold text-slate-900 dark:text-white mb-4">Mini Games</h2>
                <p className="text-base sm:text-xl text-slate-600 dark:text-slate-400">Test your knowledge with fun challenges</p>
              </div>
              <Suspense fallback={<LoadingSpinner />}>
                <MiniGames />
              </Suspense>
            </div>
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <a
        href="https://buymeacoffee.com/bazzyuk"
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-center gap-2 bg-amber-400 hover:bg-amber-500 transition-colors px-4 py-2 text-amber-950 text-xs sm:text-sm font-semibold whitespace-nowrap"
      >
        <span aria-hidden="true">☕</span>
        <span className="hidden sm:inline">If this app teaches you something, think about buying me a coffee</span>
        <span className="sm:hidden">Enjoying this? Buy me a coffee</span>
        <span className="underline underline-offset-2">buymeacoffee.com →</span>
      </a>

      <AppHeader section={section} onNavigate={setSection} isDark={isDark} onToggleDark={toggleDark} />

      <main className="pt-28 lg:pt-36">
        <AnimatePresence mode="wait">
          {renderContent()}
        </AnimatePresence>
      </main>

      <nav
        className="lg:hidden fixed bottom-4 left-4 right-4 z-40"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-xl rounded-3xl shadow-2xl shadow-slate-900/10 border border-slate-200/50 dark:border-slate-700/50 p-1">
          <div className="flex justify-between items-center gap-1">
            {NAVIGATION_ITEMS.map((item) => (
              <MobileNavButton
                key={item.section}
                active={section === item.section}
                onClick={() => setSection(item.section)}
                icon={item.icon}
                label={item.label}
                gradient={item.gradient}
              />
            ))}
          </div>
        </div>
      </nav>

    </div>
  );
};

interface DarkModeToggleProps {
  isDark: boolean;
  onToggle: () => void;
}

const DarkModeToggle: React.FC<DarkModeToggleProps> = ({ isDark, onToggle }) => (
  <motion.button
    onClick={onToggle}
    aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
    aria-pressed={isDark}
    className="relative w-16 h-9 rounded-full bg-slate-200 dark:bg-slate-600 transition-colors flex-shrink-0"
    whileTap={{ scale: 0.92 }}
  >
    <motion.div
      className="absolute top-1 left-1 w-7 h-7 rounded-full bg-white shadow-md flex items-center justify-center text-sm"
      animate={{ x: isDark ? 28 : 0 }}
      transition={{ type: 'spring', stiffness: 500, damping: 35 }}
    >
      <span aria-hidden="true">{isDark ? '🌙' : '☀️'}</span>
    </motion.div>
  </motion.button>
);

const LoadingSpinner = () => (
  <div className="flex items-center justify-center py-20">
    <div className="relative">
      <motion.div
        className="w-20 h-20 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [1, 0.5, 1],
        }}
        transition={{
          duration: 1.5,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      <motion.div
        className="absolute inset-0 w-20 h-20 rounded-full border-4 border-blue-500 border-t-transparent"
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
      />
    </div>
  </div>
);

interface MobileNavButtonProps {
  active: boolean;
  onClick: () => void;
  icon: string;
  label: string;
  gradient: string;
}

const MobileNavButton: React.FC<MobileNavButtonProps> = ({ active, onClick, icon, label, gradient }) => {
  const handleClick = () => {
    if ('vibrate' in navigator) {
      navigator.vibrate(10);
    }
    onClick();
  };

  return (
    <motion.button
      onClick={handleClick}
      whileTap={{ scale: 0.9 }}
      aria-label={`Navigate to ${label}`}
      aria-current={active ? 'page' : undefined}
      className="relative flex-1 py-2 lg:py-3 px-2 rounded-2xl transition-all min-w-[52px] min-h-[44px]"
    >
      {active && (
        <motion.div
          layoutId="activeMobileSection"
          className={`absolute inset-0 bg-gradient-to-br ${gradient} rounded-2xl shadow-lg`}
          transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
        />
      )}
      <div className={`relative flex flex-col items-center gap-1 ${active ? 'text-white' : 'text-slate-600 dark:text-slate-400'}`}>
        <span className="text-xl" aria-hidden="true">{icon}</span>
        <span className="text-[10px] font-black uppercase tracking-wider">{label}</span>
      </div>
    </motion.button>
  );
};

export default App;
